import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchMachineUpdateTask } from '../features/machines/machinesSlice';
import { formatDateTask } from '../utils/taskUtils';

const initialForm = {
  comments: '',
  status: '',
  performedByExternalProvider: false,
};

/**
 * Custom hook for managing task form state and submission
 * @param {Object} taskData - Task data object
 * @param {Function} fetchdata - Function to refresh data after form submission
 * @param {Function} handleClose - Function to close the modal
 * @param {Function} t - Translation function
 * @returns {Object} Form state and handlers
 */
export function useTaskForm(taskData, fetchdata, handleClose, t) {
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setErrorResponse('');
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({
      ...form,
      [e.target.name]: value,
    });
  };

  const handleFileChange = (file, error) => {
    setErrorResponse('');
    if (error) {
      setErrorResponse(error);
      return;
    }
    setSelectedFile(file);
  };

  const resetForm = () => {
    setForm(initialForm);
    setSelectedFile(null);
    setErrorResponse('');
  };

  const handleSubmit = async () => {
    if (!taskData) return;

    try {
      const formData = new FormData();
      if (selectedFile) {
        formData.append('file', selectedFile);
      }
      formData.append('comment', form.comments);
      formData.append('status', form.status);
      formData.append('date', formatDateTask(taskData.createdAt));
      formData.append('machine', JSON.stringify({ id: taskData.machineId }));
      formData.append('maintenance', JSON.stringify({ id: taskData.maintenanceId }));
      formData.append('title', taskData.title || taskData.maintenanceName);
      formData.append('description', taskData.description || '');
      formData.append('performedByExternalProvider', form.performedByExternalProvider);

      const today = new Date().toISOString().split('T')[0];
      if (taskData.status === 'outdated' && formatDateTask(taskData?.updatedAt) !== today) {
        if (!form.comments.trim()) {
          setErrorResponse(t('modals.commentNotEmpty'));
          return;
        }
      }

      const response = await dispatch(fetchMachineUpdateTask(formData));
      if (response.payload?.error || response.payload?.statusCode === 500) {
        setErrorResponse(response.payload.message);
      } else {
        fetchdata();
        handleClose();
        resetForm();
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setErrorResponse('An error occurred while updating the task');
    }
  };

  return {
    form,
    errorResponse,
    selectedFile,
    handleChange,
    handleFileChange,
    handleSubmit,
    resetForm,
  };
}